export const STATUS_OPTIONS = ['Active', 'Paused', 'Draft']

export const SOURCE_MEDIUM_OPTIONS = {
  'Any / Unknown': ['Any / Unknown'],
  Website: ['Homepage', 'Banner', 'Popup', 'Blog', 'Product Page', 'Promotion Page', 'Checkout', 'Header', 'Footer', 'Others'],
  Facebook: ['Organic Post', 'Story', 'Reel', 'Paid Advertisement', 'Messenger', 'Group', 'Others'],
  Instagram: ['Feed Post', 'Story', 'Reel', 'Bio Link', 'Paid Advertisement', 'Influencer', 'Others'],
  TikTok: ['Video', 'Live', 'Profile Link', 'Paid Advertisement', 'Influencer', 'Others'],
  'X (Twitter)': ['Tweet', 'Pinned Tweet', 'Paid Advertisement', 'Profile Link', 'Others'],
  WhatsApp: ['Broadcast', 'Group', 'Direct Chat', 'Status', 'Others'],
  'QR Code': ['Table Tent', 'Flyer', 'Poster', 'Packaging', 'Receipt', 'Billboard', 'Event Booth', 'Others'],
  Email: ['Newsletter', 'Promotional Email', 'Order Email', 'Membership Email', 'Others'],
  SMS: ['Marketing', 'Transactional', 'OTP', 'Others'],
  Influencer: ['Instagram', 'TikTok', 'Facebook', 'YouTube', 'Blog', 'Others'],
  Others: ['Others'],
}

export const DESTINATION_OPTIONS = ['WhatsApp Chat', 'Mini Program Home', 'Product Page', 'Promotion Page', 'Voucher Page']

const toSnake = (value = '') => String(value).trim().toLowerCase().replace(/&/g, 'and').replace(/[()]/g, '').replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '')
const toTitle = (value = '') => String(value).split('_').filter(Boolean).map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(' ')

export const statusToApi = (value) => toSnake(value || 'Active')
export const sourceToApi = (value) => value === 'Any / Unknown' ? null : toSnake(value)
export const mediumToApi = (value) => value === 'Any / Unknown' ? null : toSnake(value)
export const destinationToApi = (value) => toSnake(value || 'WhatsApp Chat')

export const statusFromApi = (value) => {
  const normalized = toSnake(value)
  if (normalized === 'active') return 'Active'
  if (normalized === 'paused') return 'Paused'
  if (normalized === 'draft') return 'Draft'
  return value || 'Active'
}

export const destinationFromApi = (value) => {
  const normalized = toSnake(value)
  if (normalized === 'whatsapp_chat') return 'WhatsApp Chat'
  if (normalized === 'mini_program_home') return 'Mini Program Home'
  if (normalized === 'product_page') return 'Product Page'
  if (normalized === 'promotion_page') return 'Promotion Page'
  if (normalized === 'voucher_page') return 'Voucher Page'
  return value ? toTitle(normalized) : 'WhatsApp Chat'
}

export const sourceFromApi = (value) => {
  const normalized = toSnake(value)
  if (!normalized || normalized === 'any' || normalized === 'unknown') return 'Any / Unknown'
  if (normalized === 'x_twitter' || normalized === 'twitter') return 'X (Twitter)'
  if (normalized === 'qr_code') return 'QR Code'
  return toTitle(normalized)
}

export const mediumFromApi = (value) => {
  const normalized = toSnake(value)
  if (!normalized || normalized === 'any' || normalized === 'unknown') return 'Any / Unknown'
  if (normalized === 'paid_advertisement') return 'Paid Advertisement'
  if (normalized === 'feed_post') return 'Feed Post'
  if (normalized === 'bio_link') return 'Bio Link'
  if (normalized === 'profile_link') return 'Profile Link'
  if (normalized === 'table_tent') return 'Table Tent'
  return toTitle(normalized)
}

export const slugify = (value) => String(value || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')

export const getTrackingCode = (item = {}) => item.tracking_code || item.trackingCode || item.wave_code || item.waveCode || item.code || item.slug || ''

export const buildWhatsAppCustomerMessage = (defaultText = '', trackingCode = '') => {
  const cleanedText = String(defaultText || '').trim()
  const cleanedCode = String(trackingCode || '').trim()
  const trackingLine = cleanedCode ? `WAVE-${cleanedCode.replace(/^WAVE-/i, '')}` : ''

  if (cleanedText && trackingLine) return `${cleanedText}

${trackingLine}`
  if (trackingLine) return trackingLine
  return cleanedText
}

export const buildWhatsAppLink = (phone = '', defaultText = '', trackingCode = '') => {
  const cleanedPhone = String(phone || '').replace(/[^0-9]/g, '') || '60123456789'
  const customerMessage = buildWhatsAppCustomerMessage(defaultText, trackingCode)
  return `https://wa.me/${cleanedPhone}${customerMessage ? `?text=${encodeURIComponent(customerMessage)}` : ''}`
}


export const formatDateTime = (value) => {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)
  return date.toLocaleString('en-MY', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export const getQrDownloadUrl = (value, size = 512) => `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&format=png&data=${encodeURIComponent(value || '')}`

export const downloadQrCode = async (value, filename = 'campaign-link-qr.png') => {
  if (!value) return

  const qrUrl = getQrDownloadUrl(value)

  try {
    const response = await fetch(qrUrl)
    if (!response.ok) throw new Error('Unable to generate QR code.')

    const blob = await response.blob()
    const objectUrl = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = objectUrl
    link.download = filename
    document.body.appendChild(link)
    link.click()
    link.remove()
    URL.revokeObjectURL(objectUrl)
  } catch (_) {
    window.open(qrUrl, '_blank', 'noopener,noreferrer')
  }
}

export const formatNumber = (value) => Number(value || 0).toLocaleString('en-MY')
export const formatMoney = (value) => `RM${Number(value || 0).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

export const getPayload = (form) => ({
  name: form.campaignName,
  description: form.description || null,
  status: statusToApi(form.status),
  start_at: form.startAt || null,
  end_at: form.endAt || null,
  destination_type: destinationToApi(form.destinationType),
  destination_value: form.destinationType === 'Mini Program Home' ? null : form.destinationDetail,
  traffic_source: sourceToApi(form.trafficSource),
  medium: mediumToApi(form.medium),
  slug: slugify(form.slug || form.campaignName),
  notes: form.notes || null,
  whatsapp_default_text: form.destinationType === 'WhatsApp Chat' ? (form.defaultWhatsAppText || null) : null,
  default_whatsapp_text: form.destinationType === 'WhatsApp Chat' ? (form.defaultWhatsAppText || null) : null,
  default_text: form.destinationType === 'WhatsApp Chat' ? (form.defaultWhatsAppText || null) : null,
  first_response_message: form.destinationType === 'WhatsApp Chat' ? (form.firstResponseMessage || null) : null,
  // Backward compatibility for the earlier WhatsApp prefilled text field.
  whatsapp_message: form.destinationType === 'WhatsApp Chat' ? (form.defaultWhatsAppText || null) : null,
})

export const getApiList = (response) => {
  const data = response?.data ?? response
  if (Array.isArray(data)) return data
  if (Array.isArray(data?.data)) return data.data
  if (Array.isArray(data?.items)) return data.items
  return []
}

export const getApiItem = (response) => response?.data?.data || response?.data || response || null

export const mapCampaignFromApi = (item = {}) => {
  const slug = item.slug || ''
  const trackingCode = getTrackingCode(item)
  const destinationType = destinationFromApi(item.destination_type || item.destinationType || item.destination)
  const source = sourceFromApi(item.traffic_source || item.trafficSource || item.source)
  const medium = mediumFromApi(item.medium)
  const status = statusFromApi(item.status)
  const baseSmartLink = item.smart_link || item.smartLink || item.short_url || item.url || (slug ? `wave.my/c/${slug}` : '-')
  const defaultWhatsAppText = item.whatsapp_default_text || item.default_whatsapp_text || item.defaultWhatsAppText || item.whatsappDefaultText || item.default_text || item.defaultText || item.prefilled_text || item.prefilledText || item.whatsapp_message || item.whatsappMessage || ''
  const firstResponseMessage = item.first_response_message || item.firstResponseMessage || item.bot_first_response_message || item.botFirstResponseMessage || item.entry_message || item.entryMessage || ''

  return {
    id: item.id,
    name: item.name || item.campaign_name || item.campaignName || '-',
    description: item.description || item.notes || '-',
    status,
    startAt: item.start_at || item.startAt || '',
    endAt: item.end_at || item.endAt || '',
    destinationType,
    destinationDetail: item.destination_value || item.destinationValue || item.destination_detail || item.destinationDetail || '',
    destination: destinationType.replace(' Chat', '').replace(' Home', ''),
    destinationMeta: destinationType.includes('WhatsApp') ? 'Chat' : destinationType.replace('Mini Program ', '') || '-',
    trafficSource: source,
    source: source === 'Any / Unknown' ? 'Any' : source,
    medium,
    slug,
    trackingCode,
    notes: item.notes || '',
    smartLink: baseSmartLink,
    defaultWhatsAppText,
    whatsappDefaultText: defaultWhatsAppText,
    firstResponseMessage,
    whatsappMessage: defaultWhatsAppText,
    whatsappLink: item.whatsapp_link || item.whatsappLink || (destinationType === 'WhatsApp Chat' ? buildWhatsAppLink(item.destination_value || item.destinationValue || item.destination_detail || item.destinationDetail || '', defaultWhatsAppText, trackingCode) : ''),
    clicks: item.clicks_count ?? item.clicks ?? item.total_clicks ?? 0,
    visitors: item.visitors_count ?? item.visitors ?? item.unique_visitors ?? 0,
    waOpens: item.whatsapp_opens_count ?? item.whatsapp_opens ?? item.wa_opens ?? 0,
    orders: item.orders_count ?? item.orders ?? 0,
    revenue: item.revenue ?? item.total_revenue ?? 0,
    createdAt: item.created_at || item.createdAt || '',
    createdBy: item.created_by_name || item.created_by || item.createdBy || '-',
  }
}

export const mapCampaignToForm = (item) => {
  const campaign = mapCampaignFromApi(item)
  return {
    campaignName: campaign.name === '-' ? '' : campaign.name,
    description: campaign.description === '-' ? '' : campaign.description,
    status: campaign.status,
    startAt: campaign.startAt ? String(campaign.startAt).slice(0, 16) : '',
    endAt: campaign.endAt ? String(campaign.endAt).slice(0, 16) : '',
    destinationType: campaign.destinationType,
    destinationDetail: campaign.destinationDetail,
    defaultWhatsAppText: campaign.defaultWhatsAppText || campaign.whatsappDefaultText || campaign.whatsappMessage || '',
    firstResponseMessage: campaign.firstResponseMessage || '',
    trafficSource: campaign.trafficSource,
    medium: campaign.medium,
    slug: campaign.slug,
    notes: campaign.notes,
  }
}

export const DEFAULT_FORM = {
  campaignName: '',
  description: '',
  status: 'Active',
  startAt: '',
  endAt: '',
  destinationType: 'WhatsApp Chat',
  destinationDetail: '+60 12-345 6789',
  defaultWhatsAppText: '',
  firstResponseMessage: '',
  trafficSource: 'Any / Unknown',
  medium: 'Any / Unknown',
  slug: '',
  notes: '',
}
